import { Box, Container, Typography, Paper } from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import { useEffect } from 'react';

const About = () => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start('visible');
  }, [controls]);

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: custom * 0.2,
        duration: 0.5,
        ease: 'easeOut'
      }
    }),
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <Box id="about-section" sx={{ 
      py: { xs: 6, sm: 7, md: 8 }, 
      px: { xs: 2, sm: 3, md: 4 },
      backgroundColor: '#f5f5f5' 
    }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <Typography 
            variant="h2" 
            sx={{ 
              mb: { xs: 3, sm: 4 }, 
              textAlign: 'center', 
              color: '#1a237e',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 'bold'
            }}>
            The MUMMY & DADDY Story
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 3 }}>
            <Box>
              <motion.div
                custom={0}
                initial="hidden"
                animate={controls}
                variants={cardVariants}
              >
                <Paper elevation={3} sx={{ 
                  p: { xs: 2.5, sm: 3, md: 4 }, 
                  borderRadius: 2,
                  height: '100%',
                  '& .MuiTypography-h5': {
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                  },
                  '& .MuiTypography-body1': {
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                    lineHeight: { xs: 1.5, sm: 1.6 }
                  }
                }}>
                  <Typography variant="h5" sx={{ mb: 3, color: '#311b92' }}>
                    Born from the Trading Floor
                  </Typography>
                  <Typography variant="body1">
                    Deep in the digital jungle of Pro Trading chatrooms, where traders swap tips, tears, and triumphs 
                    during market hours, Mummy and Daddy Coins were born. They're not just tokens—they're the embodiment 
                    of the trader's psyche, capturing those gut-wrenching and exhilarating moments when the market throws 
                    you a curveball.
                  </Typography>
                </Paper>
              </motion.div>
            </Box>

            <Box sx={{ gridColumn: { xs: '1/-1', md: 'span 6' } }}>
              <motion.div
                custom={1}
                initial="hidden"
                animate={controls}
                whileHover="hover"
                variants={cardVariants}
              >
                <Paper elevation={3} sx={{ 
                  p: { xs: 2.5, sm: 3, md: 4 }, 
                  borderRadius: 2,
                  height: '100%',
                  bgcolor: '#fff5f8',
                  '& .MuiTypography-h6': {
                    fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.5rem' }
                  },
                  '& .MuiTypography-body1': {
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                    lineHeight: { xs: 1.5, sm: 1.6 }
                  }
                }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#ff4081' }}>
                    MUMMY Coin 🌟
                  </Typography>
                  <Typography variant="body1">
                    When the market takes a nosedive and your portfolio starts looking like a horror show, who do you call? 
                    Mummy! This coin is your virtual security blanket, a lighthearted rally cry for protection when the 
                    charts turn red and panic sets in.
                  </Typography>
                </Paper>
              </motion.div>
            </Box>

            <Box sx={{ gridColumn: { xs: '1/-1', md: 'span 6' } }}>
              <motion.div
                custom={1.2}
                initial="hidden"
                animate={controls}
                whileHover="hover"
                variants={cardVariants}
              >
                <Paper elevation={3} sx={{ 
                  p: { xs: 2.5, sm: 3, md: 4 }, 
                  borderRadius: 2,
                  height: '100%',
                  bgcolor: '#fce4ec',
                  '& .MuiTypography-h6': {
                    fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.5rem' }
                  },
                  '& .MuiTypography-body1': {
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                    lineHeight: { xs: 1.5, sm: 1.6 }
                  }
                }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#f50057' }}>
                    DADDY Coin 💪
                  </Typography>
                  <Typography variant="body1">
                    But when the tides turn and opportunity knocks, it's time to say, "Come to Daddy!" Whether you're 
                    going long or short, Daddy Coin captures that swaggering confidence when you take a position and 
                    root for the price to swing your way.
                  </Typography>
                </Paper>
              </motion.div>
            </Box>

            <Box>
              <motion.div
                custom={1.4}
                initial="hidden"
                animate={controls}
                whileHover="hover"
                variants={cardVariants}
              >
                <Paper elevation={3} sx={{ 
                  p: { xs: 2.5, sm: 3, md: 4 }, 
                  borderRadius: 2,
                  height: '100%',
                  bgcolor: '#e8eaf6'
                }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1a237e' }}>
                    Our Mission
                  </Typography>
                  <Typography variant="body1">
                    We're here to inject humor into the high-stakes world of trading, reminding us all to laugh a little 
                    when our hard-earned cash seems to be sprinting for the exit. These coins are a community-driven, 
                    tongue-in-cheek salute to the emotional ups and downs of the market.
                  </Typography>
                </Paper>
              </motion.div>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default About;
